import { useAppRouter } from '@/common/route/useAppRouter';
import { useCrudActions } from '@/common/view/useCrudActions';
import { useIAPList } from '@/modules/iap/model/list';
import { ROUTE_NAME_PIGGYBANK } from '@/modules/piggybank/router';
import { piggybankService } from '@/services/piggybank';
import { useVuelidate } from '@vuelidate/core';
import { integer, minValue, numeric, required, requiredIf } from '@vuelidate/validators';
import { computed, reactive } from 'vue';
import { useRoute } from 'vue-router';

export function usePiggybankEdit() {
    const route = useRoute();
    const router = useAppRouter();
    const crudAction = useCrudActions();

    const { iapOptions, fetchIapOptions } = useIAPList();

    const model = reactive({
        id: route.params.id,
        name: null,
        packageId: '',
        price: 0,
        value: 0,
        baseLog: 0,
        fromAvgPayment: -1,
        toAvgPayment: 0,
        adsBoost: 0,
        adsBoostPercent: 0,
        adsBoostMin: 0,
        adsBoostSeconds: 0,
        totalLinked: null,
        status: null,
        image: null,
        imageStatus: null,
        imageFile: null,
        thumbnail: null,
        thumbnailStatus: null,
        thumbnailFile: null,
        canEnable: false,
        canDisable: false,
        canDelete: false
    });

    const validations = {
        imageFile: {},
        thumbnailFile: {},
        name: { required },
        packageId: { required },
        price: { required, numeric, min: minValue(1) },
        baseLog: { required, numeric, min: minValue(1) },
        value: { required, numeric, min: minValue(1) },
        adsBoost: { required, integer, min: minValue(0) },
        adsBoostPercent: { required, integer, min: minValue(0) },
        adsBoostMin: { required, integer, min: minValue(0) },
        adsBoostSeconds: { required, integer, min: minValue(0) },
        fromAvgPayment: { integer, min: minValue(-1) },
        toAvgPayment: {
            required: requiredIf(() => model.fromAvgPayment && model.fromAvgPayment >= 0),
            validValue: computed(() => minValue(model.fromAvgPayment + 1))
        }
    };

    const v$ = useVuelidate(validations, model);

    const fetch = crudAction.executeAction({
        request: () => piggybankService.item.get(model.id),
        onSuccess: ({ data }) => {
            data.fromAvgPayment = data.fromAvgPayment === -1 ? null : data.fromAvgPayment;
            data.toAvgPayment = data.toAvgPayment === -1 ? null : data.toAvgPayment;
            Object.assign(model, data);
        },
        onError: () => router.push({ name: ROUTE_NAME_PIGGYBANK.ITEM.INDEX })
    });

    const actions = {
        update: crudAction.excuteAndNotify({
            validator: v$,
            request: () =>
                piggybankService.item.update(
                    model.id,
                    model.name,
                    model.packageId,
                    model.price,
                    model.value,
                    model.baseLog,
                    model.fromAvgPayment,
                    model.toAvgPayment,
                    model.adsBoost,
                    model.adsBoostPercent,
                    model.adsBoostMin,
                    model.adsBoostSeconds,
                    model.imageFile,
                    model.thumbnailFile
                ),
            onSuccess: () => router.push({ name: ROUTE_NAME_PIGGYBANK.ITEM.INDEX })
        }),
        disable: crudAction.confirmAndDisable({
            request: () => piggybankService.item.disable(model.id),
            onSuccess: fetch
        }),
        enable: crudAction.confirmAndEnable({
            request: () => piggybankService.item.enable(model.id),
            onSuccess: fetch
        }),
        remove: crudAction.confirmAndRemove({
            request: () => piggybankService.item.remove(model.id),
            onSuccess: () => router.push({ name: ROUTE_NAME_PIGGYBANK.ITEM.INDEX })
        }),
        selectIAP: (iapId: any) => {
            iapOptions.value.forEach(item => {
                if (item.value == iapId) {
                    model.packageId = item.data.packageId;
                    model.price = item.data.price;
                    model.value = item.data.credit;
                }
            });
        }
    };

    return { model, v$, fetch, actions, iapOptions, fetchIapOptions };
}
